import React, { useState, useEffect, useMemo } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Button,
  Paper,
  IconButton,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Stack,
  Card,
  CardContent,
  Divider,
  alpha,
  useTheme,
  Tabs,
  Tab,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Autocomplete,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  PostAdd as PostIcon,
  Undo as UndoIcon,
  LocalShipping as ShippingIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Inventory as InventoryIcon,
  ThumbUp as ApproveIcon,
  ThumbDown as RejectIcon,
  HourglassTop as PendingIcon,
  Cancel as CancelIcon,
  Block as BlockIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import { useConfirm } from '../hooks/useConfirm';
import StockIssueDialog from '../components/stockIssues/StockIssueDialog';
import StockIssuePreviewDialog from '../components/stockIssues/StockIssuePreviewDialog';
import { StockIssue } from '../types/stockIssues.types';
import { stockIssuesService } from '../services/stockIssues.service';
import { itemsService } from '../services/items.service';
import { Item } from '../types/items.types';

const translations = {
  en: {
    title: 'Stock Issues',
    addIssue: 'New Issue',
    search: 'Search issues...',
    all: 'All',
    pending: 'Pending',
    draft: 'Draft',
    posted: 'Posted',
    rejected: 'Rejected',
    totalIssues: 'Total Issues',
    totalValue: 'Total Value',
    warehouse: 'Warehouse',
    branch: 'Branch',
    date: 'Date',
    items: 'Items',
    status: 'Status',
    actions: 'Actions',
    view: 'View',
    edit: 'Edit',
    delete: 'Delete',
    post: 'Post Issue',
    unpost: 'Unpost',
    approve: 'Approve & Issue',
    reject: 'Reject',
    noData: 'No issues found',
    confirmDelete: 'Delete this issue?',
    confirmPost: 'Post this issue? Stock will be transferred.',
    confirmUnpost: 'Unpost this issue? This will reverse stock transfer.',
    confirmApprove: 'Approve this issue? Journal entry will be created.',
    rejectDialogTitle: 'Reject Stock Issue',
    rejectReason: 'Rejection Reason (optional)',
    rejectConfirm: 'Confirm Reject',
    journalEntry: 'J.E.',
    pendingApproval: 'Pending Approval',
    approvedBy: 'Approved',
    rejectedStatus: 'Rejected',
    requestedBy: 'Requested by Branch',
    awaitingWarehouse: 'Awaiting warehouse keeper approval',
    fromDate: 'From Date',
    toDate: 'To Date',
  },
  ar: {
    title: 'إصدارات المخزون',
    addIssue: 'إصدار جديد',
    search: 'بحث في الإصدارات...',
    all: 'الكل',
    pending: 'معلّق',
    draft: 'مسودة',
    posted: 'مرحّل',
    rejected: 'مرفوض',
    totalIssues: 'إجمالي الإصدارات',
    totalValue: 'إجمالي القيمة',
    warehouse: 'المخزن',
    branch: 'الفرع',
    date: 'التاريخ',
    items: 'الأصناف',
    status: 'الحالة',
    actions: 'الإجراءات',
    view: 'عرض',
    edit: 'تعديل',
    delete: 'حذف',
    post: 'ترحيل',
    unpost: 'إلغاء ترحيل',
    approve: 'موافقة وصرف',
    reject: 'رفض',
    noData: 'لا توجد إصدارات',
    confirmDelete: 'حذف هذا الإصدار؟',
    confirmPost: 'ترحيل الإصدار؟ سيتم نقل المخزون.',
    confirmUnpost: 'إلغاء الترحيل؟ سيتم عكس نقل المخزون.',
    confirmApprove: 'الموافقة على هذا الطلب؟ سيتم إنشاء القيد المحاسبي.',
    rejectDialogTitle: 'رفض طلب الصرف',
    rejectReason: 'سبب الرفض (اختياري)',
    rejectConfirm: 'تأكيد الرفض',
    journalEntry: 'ق.م.',
    pendingApproval: 'بانتظار الموافقة',
    approvedBy: 'تمت الموافقة',
    rejectedStatus: 'مرفوض',
    requestedBy: 'طلب من الفرع',
    awaitingWarehouse: 'بانتظار موافقة أمين المخزن',
    fromDate: 'من تاريخ',
    toDate: 'إلى تاريخ',
  },
};

const StockIssuesPage: React.FC = () => {
  const { language } = useLanguage();
  const theme = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();
  const confirm = useConfirm();

  // ✅ استخراج صلاحيات المستخدم من التوكن
  const { userBranchIds, userWarehouseIds, isSuperAdmin } = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return { userBranchIds: [] as number[], userWarehouseIds: [] as number[], isSuperAdmin: false };
      const payload = JSON.parse(atob(token.split('.')[1]));
      const primaryBranch = payload.BranchId ? [Number(payload.BranchId)] : [];
      const allowedBranches = payload.AllowedBranchId
        ? (Array.isArray(payload.AllowedBranchId) ? payload.AllowedBranchId.map(Number) : [Number(payload.AllowedBranchId)])
        : [];
      const branchIds = Array.from(new Set([...primaryBranch, ...allowedBranches]));
      const keeperClaims = payload.KeeperWarehouseId;
      const warehouseIds = Array.isArray(keeperClaims) ? keeperClaims.map(Number) : keeperClaims ? [Number(keeperClaims)] : [];
      // سوبر أدمن: لا فروع ولا مخازن محددة
      const superAdmin = branchIds.length === 0 && warehouseIds.length === 0;
      return { userBranchIds: branchIds, userWarehouseIds: warehouseIds, isSuperAdmin: superAdmin };
    } catch {
      return { userBranchIds: [] as number[], userWarehouseIds: [] as number[], isSuperAdmin: false };
    }
  })();

  // ✅ هل المستخدم أمين مخزن؟
  const isWarehouseKeeper = userWarehouseIds.length > 0;
  // ✅ هل المستخدم مدير فرع فقط؟
  const isBranchManager = userBranchIds.length > 0 && userWarehouseIds.length === 0;
  
  const canEditIssue = (issue: StockIssue) => {
    if (isSuperAdmin) return true;
    if (issue.warehouseId && userWarehouseIds.includes(issue.warehouseId)) return true;
    if (issue.branchId && userBranchIds.includes(issue.branchId)) return true;
    return false;
  };

  // ✅ أمين المخزن أو السوبر أدمن يستطيع الموافقة/الرفض
  const canApproveReject = (issue: StockIssue) => {
    if (isSuperAdmin) return true;
    if (isWarehouseKeeper && issue.warehouseId && userWarehouseIds.includes(issue.warehouseId)) return true;
    return false;
  };
  
  const userRoles = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return [] as string[];
      const payload = JSON.parse(atob(token.split('.')[1]));
      const roles = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      return roles ? (Array.isArray(roles) ? roles : [roles]) : [];
    } catch { return [] as string[]; }
  })();
  const canPost = (() => { try { const token = localStorage.getItem('token'); if (!token) return false; const payload = JSON.parse(atob(token.split('.')[1])); const perms = payload.Permission ? (Array.isArray(payload.Permission) ? payload.Permission : [payload.Permission]) : []; return perms.some((p: string) => p.endsWith('_POST')); } catch { return false; } })();

  const [issues, setIssues] = useState<StockIssue[]>([]);
  const [filteredIssues, setFilteredIssues] = useState<StockIssue[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<StockIssue | undefined>();
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedIssueForMenu, setSelectedIssueForMenu] = useState<StockIssue | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [issueToPreview, setIssueToPreview] = useState<StockIssue | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // ✅ NEW: حالة دايلوج الرفض
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [issueToReject, setIssueToReject] = useState<StockIssue | null>(null);

  // ✅ NEW: فلتر التاريخ
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // ✅ NEW: فلتر الصنف
  const [itemFilter, setItemFilter] = useState<Item | null>(null);
  const [itemsList, setItemsList] = useState<Item[]>([]);

  useEffect(() => {
    loadIssues();
    itemsService.getAll().then(setItemsList).catch(() => {});
  }, []);

  // ✅ Auto-open issue preview when ?openId=X is in the URL (used by Data Integrity report)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('openId');
    if (!openId) return;
    const id = parseInt(openId, 10);
    if (!Number.isFinite(id)) return;
    (async () => {
      try {
        const full = await stockIssuesService.getById(id);
        setIssueToPreview(full);
        setPreviewDialogOpen(true);
      } catch {
        toast.error('Failed to open issue');
      }
    })();
  }, []);

  useEffect(() => {
    filterIssues();
  }, [searchTerm, issues, activeTab, fromDate, toDate, itemFilter]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, activeTab, fromDate, toDate, itemFilter]);

  const paginatedIssues = useMemo(
    () => filteredIssues.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredIssues, page, rowsPerPage]
  );

  const loadIssues = async () => {
    setLoading(true);
    try {
      const data = await stockIssuesService.getAll();
      const filteredData = data.filter((issue: StockIssue) => canEditIssue(issue));
      setIssues(filteredData);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load issues');
    } finally {
      setLoading(false);
    }
  };

  // ✅ UPDATED: فلترة حسب التابات + التاريخ + ترتيب حسب رقم السند
  const filterIssues = () => {
    let filtered = issues;

    if (activeTab === 1) {
      filtered = filtered.filter(issue => issue.status === 'Pending');
    } else if (activeTab === 2) {
      filtered = filtered.filter(issue => issue.status === 'Draft');
    } else if (activeTab === 3) {
      filtered = filtered.filter(issue => issue.status === 'Posted');
    } else if (activeTab === 4) {
      filtered = filtered.filter(issue => issue.status === 'Rejected');
    }

    // ✅ فلتر التاريخ
    if (fromDate) {
      filtered = filtered.filter(issue => issue.issueDate.split('T')[0] >= fromDate);
    }
    if (toDate) {
      filtered = filtered.filter(issue => issue.issueDate.split('T')[0] <= toDate);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        issue =>
          issue.issueNumber.toLowerCase().includes(term) ||
          issue.warehouseName?.toLowerCase().includes(term) ||
          issue.branchName?.toLowerCase().includes(term)
      );
    }

    // ✅ فلتر الصنف (يقابل أي سطر في تفاصيل السند)
    if (itemFilter) {
      filtered = filtered.filter(issue =>
        (issue.details || []).some(d => d.itemId === itemFilter.itemId)
      );
    }

    // ✅ ترتيب حسب رقم السند تنازلياً (الأحدث أولاً)
    filtered.sort((a, b) => b.issueNumber.localeCompare(a.issueNumber, undefined, { numeric: true }));

    setFilteredIssues(filtered);
  };

  const handleAdd = () => {
    setSelectedIssue(undefined);
    setDialogOpen(true);
  };

  const handleView = async (issue: StockIssue) => {
    handleCloseMenu();
    try {
      const fullIssue = await stockIssuesService.getById(issue.issueId);
      setIssueToPreview(fullIssue);
      setPreviewDialogOpen(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load issue details');
    }
  };

  const handleEdit = (issue: StockIssue) => {
    setSelectedIssue(issue);
    setDialogOpen(true);
    handleCloseMenu();
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({ message: t.confirmDelete, severity: 'error' });
    if (!ok) { handleCloseMenu(); return; }
    try {
      await stockIssuesService.delete(id);
      loadIssues();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete');
    }
    handleCloseMenu();
  };

  const handlePost = async (id: number) => {
    const ok = await confirm({ message: t.confirmPost, severity: 'warning' });
    if (!ok) { handleCloseMenu(); return; }
    try {
      await stockIssuesService.postIssue(id);
      loadIssues();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to post');
    }
    handleCloseMenu();
  };

  const handleUnpost = async (id: number) => {
    const ok = await confirm({ message: t.confirmUnpost, severity: 'warning' });
    if (!ok) { handleCloseMenu(); return; }
    try {
      await stockIssuesService.unpostIssue(id);
      loadIssues();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to unpost');
    }
    handleCloseMenu();
  };

  // ✅ NEW: موافقة أمين المخزن
  const handleApprove = async (id: number) => {
    const ok = await confirm({ message: t.confirmApprove, severity: 'warning' });
    if (!ok) { handleCloseMenu(); return; }
    try {
      await stockIssuesService.approve(id);
      loadIssues();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to approve');
    }
    handleCloseMenu();
  };

  // ✅ NEW: فتح دايلوج الرفض
  const handleOpenRejectDialog = (issue: StockIssue) => {
    setIssueToReject(issue);
    setRejectReason('');
    setRejectDialogOpen(true);
    handleCloseMenu();
  };

  // ✅ NEW: تأكيد الرفض
  const handleConfirmReject = async () => {
    if (!issueToReject) return;
    try {
      await stockIssuesService.reject(issueToReject.issueId, rejectReason || undefined);
      setRejectDialogOpen(false);
      setIssueToReject(null);
      setRejectReason('');
      loadIssues();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reject');
    }
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, issue: StockIssue) => {
    setAnchorEl(event.currentTarget);
    setSelectedIssueForMenu(issue);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedIssueForMenu(null);
  };

  // ✅ UPDATED: إحصائيات تشمل Pending و Rejected
  const calculateStats = () => {
    const total = issues.length;
    const totalValue = issues.reduce((sum, issue) => 
      sum + (issue.details?.reduce((s, d) => s + (d.totalCost || 0), 0) || 0), 0
    );
    const pendingCount = issues.filter(issue => issue.status === 'Pending').length;
    const draftCount = issues.filter(issue => issue.status === 'Draft').length;
    const postedCount = issues.filter(issue => issue.status === 'Posted').length;
    const rejectedCount = issues.filter(issue => issue.status === 'Rejected').length;

    return { total, totalValue, pendingCount, draftCount, postedCount, rejectedCount };
  };

  const stats = calculateStats();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ar' ? 'ar-KW' : 'en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // ✅ NEW: ألوان وأيقونات حسب الحالة
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'Pending':
        return { color: 'warning' as const, icon: <PendingIcon />, borderColor: 'warning.main', bgColor: 'warning.light', label: t.pending };
      case 'Draft':
        return { color: 'info' as const, icon: <CheckCircleIcon />, borderColor: 'info.main', bgColor: 'info.light', label: t.draft };
      case 'Posted':
        return { color: 'success' as const, icon: <CheckCircleIcon />, borderColor: 'success.main', bgColor: 'success.light', label: t.posted };
      case 'Rejected':
        return { color: 'error' as const, icon: <BlockIcon />, borderColor: 'error.main', bgColor: 'error.light', label: t.rejected };
      default:
        return { color: 'default' as const, icon: <ScheduleIcon />, borderColor: 'grey.400', bgColor: 'grey.200', label: status };
    }
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ width: 56, height: 56, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <ShippingIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{t.title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {stats.total} {language === 'ar' ? 'إصدار' : 'issues'}
              {stats.pendingCount > 0 && (
                <Chip label={`${stats.pendingCount} ${t.pending}`} size="small" color="warning" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
              )}
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadIssues} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            size="large"
            sx={{
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
              px: 4,
              py: 1.5,
            }}
          >
            {t.addIssue}
          </Button>
        </Box>
      </Stack>

      {/* ✅ UPDATED: كروت الإحصائيات تشمل Pending و Rejected */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.totalIssues}</Typography>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>{stats.total}</Typography>
              </Box>
              <InventoryIcon sx={{ fontSize: 50, opacity: 0.3 }} />
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.totalValue}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{stats.totalValue.toFixed(3)}</Typography>
                <Typography variant="caption">KD</Typography>
              </Box>
              <TrendingUpIcon sx={{ fontSize: 50, opacity: 0.3 }} />
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #ff9a56 0%, #ff6a00 100%)', color: 'white' }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.pending}</Typography>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>{stats.pendingCount}</Typography>
              </Box>
              <PendingIcon sx={{ fontSize: 50, opacity: 0.3 }} />
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
          <CardContent>
            <Stack direction="row" spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{stats.draftCount}</Typography>
                <Typography variant="caption">{t.draft}</Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{stats.postedCount}</Typography>
                <Typography variant="caption">{t.posted}</Typography>
              </Box>
              {stats.rejectedCount > 0 && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#fee2e2' }}>{stats.rejectedCount}</Typography>
                    <Typography variant="caption">{t.rejected}</Typography>
                  </Box>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* ✅ UPDATED: تابات تشمل Pending و Rejected */}
      <Paper elevation={3} sx={{ mb: 3, background: theme.palette.mode === 'dark' ? 'linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%)' : 'linear-gradient(135deg, #e0e7ff 0%, #cffafe 100%)', borderRadius: 3 }}>
        <Stack spacing={2} sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              fullWidth
              placeholder={t.search}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ backgroundColor: 'background.paper', borderRadius: 2, flex: 1.6 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
              }}
            />
            <Autocomplete
              size="medium"
              sx={{ backgroundColor: 'background.paper', borderRadius: 2, minWidth: 240, flex: 1 }}
              options={itemsList}
              value={itemFilter}
              onChange={(_, v) => setItemFilter(v)}
              getOptionLabel={(o) => `${o.itemCode} — ${isRTL ? (o.itemNameAr || o.itemName) : o.itemName}`}
              isOptionEqualToValue={(o, v) => o.itemId === v.itemId}
              filterOptions={(opts, state) => {
                const q = state.inputValue.toLowerCase().trim();
                if (!q) return opts.slice(0, 100);
                return opts.filter(o =>
                  o.itemCode.toLowerCase().includes(q) ||
                  (o.itemName || '').toLowerCase().includes(q) ||
                  (o.itemNameAr || '').includes(state.inputValue)
                ).slice(0, 200);
              }}
              renderOption={(props, o) => (
                <li {...props} key={o.itemId}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{o.itemCode}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {isRTL ? (o.itemNameAr || o.itemName) : o.itemName}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={isRTL ? 'تصفية بالصنف' : 'Filter by Item'}
                  placeholder={isRTL ? 'ابحث عن صنف...' : 'Search item...'}
                />
              )}
              clearOnEscape
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.fromDate}
                value={fromDate ? new Date(fromDate) : null}
                onChange={(date) => {
                  const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                  setFromDate(newVal);
                  if (toDate && newVal && toDate < newVal) setToDate('');
                }}
                format="dd/MM/yyyy"
                minDate={minDate}
                maxDate={toDate ? new Date(toDate) : maxDate}
                views={['year', 'month', 'day']}
                openTo="day"
                slotProps={{ textField: { sx: { backgroundColor: 'background.paper', borderRadius: 2, minWidth: 170 } } }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.toDate}
                value={toDate ? new Date(toDate) : null}
                onChange={(date) => {
                  const newVal = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                  setToDate(newVal);
                  if (fromDate && newVal && fromDate > newVal) setFromDate('');
                }}
                format="dd/MM/yyyy"
                minDate={fromDate ? new Date(fromDate) : minDate}
                maxDate={maxDate}
                views={['year', 'month', 'day']}
                openTo="day"
                slotProps={{ textField: { sx: { backgroundColor: 'background.paper', borderRadius: 2, minWidth: 170 } } }}
              />
            </LocalizationProvider>
          </Stack>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label={<Badge badgeContent={stats.total} color="primary"><span>{t.all}</span></Badge>} />
            <Tab label={<Badge badgeContent={stats.pendingCount} color="warning"><span>{t.pending}</span></Badge>} />
            <Tab label={<Badge badgeContent={stats.draftCount} color="info"><span>{t.draft}</span></Badge>} />
            <Tab label={<Badge badgeContent={stats.postedCount} color="success"><span>{t.posted}</span></Badge>} />
            {stats.rejectedCount > 0 && (
              <Tab label={<Badge badgeContent={stats.rejectedCount} color="error"><span>{t.rejected}</span></Badge>} />
            )}
          </Tabs>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <Stack spacing={2}>
          {paginatedIssues.map((issue, idx) => {
            const statusConfig = getStatusConfig(issue.status || 'Pending');
            return (
              <Fade in timeout={300 + idx * 100} key={issue.issueId}>
                <Card elevation={3} sx={{
                  transition: 'all 0.3s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 8 },
                  border: 2,
                  borderColor: statusConfig.borderColor,
                  borderLeft: 6,
                  borderLeftColor: statusConfig.borderColor,
                  ...(issue.status === 'Pending' && {
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.warning.main, 0.4)}` },
                      '70%': { boxShadow: `0 0 0 10px ${alpha(theme.palette.warning.main, 0)}` },
                      '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.warning.main, 0)}` },
                    },
                  }),
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ width: 50, height: 50, bgcolor: `${statusConfig.color}.main` }}>
                          <ShippingIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>{issue.issueNumber}</Typography>
                          <Typography variant="body2" component="div" color="text.secondary">{formatDate(issue.issueDate)}</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={statusConfig.label}
                          color={statusConfig.color}
                          icon={statusConfig.icon}
                          sx={{ fontWeight: 600, minWidth: 100 }}
                        />
                        {/* ✅ Pending: رسالة انتظار موافقة أمين المخزن */}
                        {issue.status === 'Pending' && (
                          <Tooltip title={t.awaitingWarehouse}>
                            <Chip
                              label={isRTL ? 'بانتظار أمين المخزن' : 'Awaiting Keeper'}
                              size="small"
                              color="warning"
                              variant="outlined"
                              icon={<PendingIcon />}
                            />
                          </Tooltip>
                        )}
                        {/* ✅ Draft بعد الموافقة: جاهز للترحيل */}
                        {issue.status === 'Draft' && issue.journalEntryId && (
                          <Chip
                            label={`${t.journalEntry} ${issue.journalEntryNumber || 'JE'} (#${issue.journalEntryId})`}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        )}
                        {/* ✅ أزرار سريعة للموافقة/الرفض لأمين المخزن */}
                        {(issue.status === 'Pending' || issue.status === 'Rejected') && canApproveReject(issue) && (
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title={t.approve}>
                              <IconButton
                                size="small"
                                onClick={() => handleApprove(issue.issueId)}
                                sx={{
                                  bgcolor: alpha(theme.palette.success.main, 0.1),
                                  '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.25) },
                                }}
                              >
                                <ApproveIcon fontSize="small" color="success" />
                              </IconButton>
                            </Tooltip>
                            {issue.status === 'Pending' && (
                            <Tooltip title={t.reject}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenRejectDialog(issue)}
                                sx={{
                                  bgcolor: alpha(theme.palette.error.main, 0.1),
                                  '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.25) },
                                }}
                              >
                                <RejectIcon fontSize="small" color="error" />
                              </IconButton>
                            </Tooltip>
                            )}
                          </Stack>
                        )}
                        <IconButton onClick={(e) => handleOpenMenu(e, issue)}><MoreVertIcon /></IconButton>
                      </Stack>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t.warehouse}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{isRTL ? issue.warehouseNameAr : issue.warehouseName}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t.branch}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{isRTL ? issue.branchNameAr : issue.branchName}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t.items}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>{issue.details.length}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Fade>
            );
          })}
          {filteredIssues.length === 0 && (
            <Paper sx={{ p: 8, textAlign: 'center', bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
              <ShippingIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">{t.noData}</Typography>
            </Paper>
          )}

          {filteredIssues.length > 0 && (
            <Paper elevation={2} sx={{ mt: 1 }}>
              <TablePagination
                component="div"
                count={filteredIssues.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage={isRTL ? 'صفوف في الصفحة:' : 'Rows per page:'}
                labelDisplayedRows={({ from, to, count }) =>
                  isRTL ? `${from}–${to} من ${count}` : `${from}–${to} of ${count}`
                }
                sx={{ direction: isRTL ? 'rtl' : 'ltr' }}
              />
            </Paper>
          )}
        </Stack>
      )}

      {/* ✅ UPDATED: قائمة الإجراءات حسب الدور والحالة */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu} PaperProps={{ elevation: 3, sx: { minWidth: 220, mt: 1 } }}>
        {/* عرض — للجميع */}
        <MenuItem onClick={() => selectedIssueForMenu && handleView(selectedIssueForMenu)}>
          <ViewIcon fontSize="small" sx={{ mr: 1 }} color="primary" />
          <Typography color="primary.main">{t.view}</Typography>
        </MenuItem>
        <Divider />

        {/* ✅ Pending/Rejected: موافقة — أمين المخزن والسوبر أدمن فقط */}
        {(selectedIssueForMenu?.status === 'Pending' || selectedIssueForMenu?.status === 'Rejected') && canApproveReject(selectedIssueForMenu) && (
          <MenuItem onClick={() => handleApprove(selectedIssueForMenu.issueId)}>
            <ApproveIcon fontSize="small" sx={{ mr: 1 }} color="success" />
            <Typography color="success.main" fontWeight={600}>{t.approve}</Typography>
          </MenuItem>
        )}
        {/* ✅ Pending فقط: رفض */}
        {selectedIssueForMenu?.status === 'Pending' && canApproveReject(selectedIssueForMenu) && (
          <MenuItem onClick={() => handleOpenRejectDialog(selectedIssueForMenu)}>
            <RejectIcon fontSize="small" sx={{ mr: 1 }} color="error" />
            <Typography color="error.main" fontWeight={600}>{t.reject}</Typography>
          </MenuItem>
        )}
        {(selectedIssueForMenu?.status === 'Pending' || selectedIssueForMenu?.status === 'Rejected') && canApproveReject(selectedIssueForMenu) && <Divider />}

        {/* ✅ Pending: تعديل — أمين المخزن أو مدير الفرع أو سوبر أدمن */}
        {selectedIssueForMenu?.status === 'Pending' && canEditIssue(selectedIssueForMenu!) && (
          <MenuItem onClick={() => handleEdit(selectedIssueForMenu!)}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />{t.edit}
          </MenuItem>
        )}
        {/* ✅ Draft (بعد الموافقة): تعديل فقط للسوبر أدمن/محاسبين لأن القيد المحاسبي أُنشئ */}
        {selectedIssueForMenu?.status === 'Draft' && isSuperAdmin && (
          <MenuItem onClick={() => handleEdit(selectedIssueForMenu!)}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />{t.edit}
          </MenuItem>
        )}

        {/* ✅ Pending: حذف — مدير الفرع أو أمين المخزن أو سوبر أدمن */}
        {selectedIssueForMenu?.status === 'Pending' && canEditIssue(selectedIssueForMenu!) && (
          <MenuItem onClick={() => handleDelete(selectedIssueForMenu!.issueId)}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} color="error" />
            <Typography color="error">{t.delete}</Typography>
          </MenuItem>
        )}
        {/* ✅ Draft: حذف فقط للسوبر أدمن */}
        {selectedIssueForMenu?.status === 'Draft' && isSuperAdmin && (
          <MenuItem onClick={() => handleDelete(selectedIssueForMenu!.issueId)}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} color="error" />
            <Typography color="error">{t.delete}</Typography>
          </MenuItem>
        )}

        {/* Draft: ترحيل — سوبر أدمن فقط */}
        {selectedIssueForMenu?.status === 'Draft' && canPost && canEditIssue(selectedIssueForMenu) && (
          <MenuItem onClick={() => handlePost(selectedIssueForMenu.issueId)}>
            <PostIcon fontSize="small" sx={{ mr: 1 }} color="success" />
            <Typography color="success.main">{t.post}</Typography>
          </MenuItem>
        )}

        {/* Posted: إلغاء ترحيل — سوبر أدمن فقط */}
        {selectedIssueForMenu?.status === 'Posted' && canPost && (
          <MenuItem onClick={() => handleUnpost(selectedIssueForMenu.issueId)}>
            <UndoIcon fontSize="small" sx={{ mr: 1 }} color="warning" />
            <Typography color="warning.main">{t.unpost}</Typography>
          </MenuItem>
        )}
      </Menu>

      {/* ✅ NEW: دايلوج الرفض مع سبب */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <RejectIcon />
          {t.rejectDialogTitle}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 2 }}>
          {issueToReject && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Chip label={issueToReject.issueNumber} color="primary" />
                <Typography variant="body2" color="text.secondary">
                  {isRTL ? issueToReject.branchNameAr : issueToReject.branchName}
                </Typography>
              </Stack>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t.rejectReason}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={isRTL ? 'مثال: الكمية المطلوبة غير متوفرة حالياً' : 'e.g. Requested quantity not available'}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRejectDialogOpen(false)} variant="outlined">
            {isRTL ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button onClick={handleConfirmReject} variant="contained" color="error" startIcon={<RejectIcon />}>
            {t.rejectConfirm}
          </Button>
        </DialogActions>
      </Dialog>

      <StockIssueDialog open={dialogOpen} onClose={() => setDialogOpen(false)} stockIssue={selectedIssue} onSave={loadIssues} />
      <StockIssuePreviewDialog open={previewDialogOpen} onClose={() => { setPreviewDialogOpen(false); setIssueToPreview(null); }} issue={issueToPreview} onEdit={() => { if (issueToPreview) { setPreviewDialogOpen(false); handleEdit(issueToPreview); } }} />
    </Box>
  );
};

export default StockIssuesPage;